import React, { useState } from "react";
import styled from "styled-components";
import FormButton, { NegativeFormButton } from "../servicecalls/FormButton";
import { registerPlugin } from "react-filepond";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import Services from "../../services/Services";
import IAttachmentRepository from "../../services/IAttachmentRepository";
import Skeleton from "@material-ui/lab/Skeleton";
import DownloadIcon from "../../img/baseline_get_app_white_18dp.png";
import FilePondComponent from "./FilePondComponent";
import IAuthenticatedComponentProps from "../../models/IAuthenticatedComponent";
import { connect } from "react-redux";
import { IApplicationState } from "../../store";
import { selectAuth } from "../../store/auth/selectors";
import snackbarService from "../../services/ui-messaging/SnackbarService";

import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import { AttachmentTypes, getAttachmentTypeString } from "../../functions/getAttachmentType";

// note: allowRevert=false does not remove the remove button. since there is
// also not a download button, we are doing some trickery to swap out the revert
// button for a download button.

enum FileOrigin {
  INPUT = 1,
  LIMBO = 2,
  LOCAL = 3,
}

registerPlugin(FilePondPluginImagePreview);

const FormWrapper = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  display: flex;
  position: relative;
  flex-grow: 2;
  margin: 10px;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  min-height: 100px;

  & p {
    margin: 5px;
    text-align: center;
    max-width: 600px;
  }

  & .filepond--file-action-button {
    cursor: pointer;
  }

  & .item-local svg {
    display: none;
  }

  & .item-local::before {
    content: url(${DownloadIcon});
    vertical-align: middle;
  }
`;

const DropBox = styled(FilePondComponent)`
  min-width: 300px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

export interface IAttachmentFormProps extends IAuthenticatedComponentProps {
  rmsObjectId: string;
  rmsObjectIsOpen: boolean;
  onClose?: () => void;
  attachmentParentType: AttachmentTypes;
  isInstantUpload: boolean;
  rmsObjectClosedMessage: string;
}

const AttachmentForm = (props: IAttachmentFormProps) => {
  const { onClose, rmsObjectId, rmsObjectIsOpen, attachmentParentType, isInstantUpload, rmsObjectClosedMessage, token } = props;
  const [api, setApi] = React.useState<IAttachmentRepository | undefined>();
  const [files, setFiles] = useState<any[] | undefined>();
  const parentType = getAttachmentTypeString(attachmentParentType);

  React.useEffect(() => {
    const init = async () => {
      let repo = await new Services().AttachmentRepository();
      setApi(repo);

      try {
        const existing = await repo.list(token, parentType, rmsObjectId);
        setFiles(
          existing.map(a => ({
            source: a.ID,
            options: {
              type: "local",
              file: {
                name: a.Name,
                size: a.Size,
                type: a.Type,
              },
            },
          })),
        );
      } catch (err) {
        snackbarService.error(err);
      }
    };
    init();
  }, []);

  const handleAdd = (evt: any, detail: any) => {
    if (detail.origin === FileOrigin.LOCAL) {
      let li = document.getElementById(`filepond--item-${detail.id}`);
      if (li) {
        let x = li.querySelector(".filepond--action-remove-item");
        if (x) {
          let xDiv = x as HTMLElement;
          xDiv.className += " item-local";
        }
      }
    }
  };

  const downloadItem = async (item: any): Promise<void> => {
    if (api) {
      try {
        await api.download(token, parentType, rmsObjectId, item.serverId, item.name);
      } catch (err) {
        snackbarService.error(err);
      }
    }
  };

  const handleBeforeRemove = (item: any): boolean => {
    // we are transforming this hook into a download button because there isn't one

    // only allow removal of new items before uploading
    if (item.status === 2) {
      return true;
    }

    downloadItem(item);
    return false;
  };

  const cancel = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <FormWrapper>
      <Container>
        {!!api && !!files ? (
          <DropBox
            onaddfile={handleAdd}
            allowMultiple={true}
            allowDrop={rmsObjectIsOpen}
            allowBrowse={rmsObjectIsOpen}
            labelIdle={rmsObjectIsOpen ? 'Drag & Drop your files or <span class="filepond--label-action"> Browse </span>' : rmsObjectClosedMessage}
            files={files}
            onupdatefiles={setFiles}
            beforeRemoveFile={handleBeforeRemove}
            instantUpload={isInstantUpload}
            allowRevert={false}
            chunkUploads={true}
            chunkSize={4096}
            chunkForce={true}
            server={api.getServerConfig(token, parentType, rmsObjectId)}
          />
        ) : (
          <Skeleton height="100px"></Skeleton>
        )}
      </Container>
      <ButtonRow>
        <NegativeFormButton onClick={cancel}>Close</NegativeFormButton>
      </ButtonRow>
    </FormWrapper>
  );
};

export default connect((state: IApplicationState) => ({
  ...selectAuth(state),
}))(AttachmentForm);
